import type { CalculationOut } from "@/api";
import { Button } from "@/components/Button";
import { formatMoney } from "@/lib/format";

import styles from "./CalculationScreen.module.css";
import own from "./SupplierTable.module.css";

/**
 * Every active supplier, priced against the same parts list (D-6). The
 * engine's proposal is the cheapest complete one; a person may choose
 * another — that is a parameter of the calculation (`supplierOverride`), so
 * choosing reruns it. „Vorschlag" and „gewählt" are words, not colours: on
 * an ordinary Vorgang they sit on the same row.
 *
 * `disabled` (the screen is recalculating, the calculation is released, or
 * the Vorgang is frozen) removes the buttons rather than greying them out —
 * a disabled `<button>` still answers `getByRole("button")`, and once
 * released the row is not a place to click at all (D-11).
 */
export function SupplierTable({
  calc,
  disabled,
  onChoose,
}: {
  calc: CalculationOut;
  disabled: boolean;
  onChoose: (supplierOverride: string | null) => void;
}) {
  if (calc.alternatives.length === 0) {
    // No comparison, a supplier, and a price on every grate: each one is
    // priced from a single fixed source (Barfußrost: Meiser 30x10 ×1,5) —
    // there was nothing to compare, and nothing failed. A grate without a
    // price beside it (a Pressrost left on a Barfuß Vorgang after a type
    // change) is exactly what nobody could price (review 23.10.2026).
    const fixed =
      calc.supplier !== null && calc.lines.every((line) => line.kind !== "grating" || line.vkTotal !== null);
    return (
      <p className={styles.note}>
        {fixed
          ? `Diese Roste bepreist ${calc.supplier} fest — einen Lieferantenvergleich gibt es für sie nicht.`
          : "Kein Lieferant kann diese Stückliste vollständig bepreisen."}
      </p>
    );
  }

  /**
   * The mirror carries whatever spelling the supplier's own row used
   * ("verzinkt" for one, "Verzinkt" for another) — matched case-insensitively
   * against the same options `ParameterBar`'s material select offers, so the
   * table shows the one label a person already reads there rather than the
   * raw storage text (S-4). A value the options do not know at all is shown
   * as itself, same fallback `SelectField` uses for a stored value it does
   * not carry.
   */
  function materialLabel(value: string): string {
    const known = calc.options.materials.find(
      (option) => option.value.toLowerCase() === value.toLowerCase(),
    );
    return known?.label ?? value;
  }

  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <caption className="visually-hidden">Lieferanten im Vergleich, Einkauf der Roste</caption>
        <thead>
          <tr>
            <th scope="col" className="label">Lieferant</th>
            <th scope="col" className="label">Material</th>
            <th scope="col" className={`label ${styles.moneyHead}`}>EK Roste</th>
            <th scope="col" className="label">Stand</th>
            {/* The positioned cell holds the hidden word inside the scrolling
                frame (R4-1) — see SupplierTable.module.css. */}
            <th scope="col" className={`label ${own.choiceHead}`}>
              <span className="visually-hidden">Wahl</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {calc.alternatives.map((alternative) => {
            const chosen = alternative.supplier === calc.supplier;
            const proposed = alternative.supplier === calc.supplierProposed;
            const words = [proposed ? "Vorschlag" : null, chosen ? "gewählt" : null]
              .filter((word): word is string => word !== null)
              .join(" · ");
            return (
              <tr key={alternative.supplier} className={chosen ? styles.chosen : undefined}>
                <th scope="row">{alternative.supplier}</th>
                <td>{materialLabel(alternative.material)}</td>
                <td className={`num ${styles.money}`}>{formatMoney(alternative.ekTotal)}</td>
                <td>
                  {alternative.complete ? (
                    words === "" ? "—" : words
                  ) : (
                    <span className={styles.blockedCell}>
                      unvollständig — {alternative.reason ?? "ohne Grund"}
                    </span>
                  )}
                </td>
                <td>
                  {!disabled && alternative.complete && !chosen ? (
                    <Button onClick={() => onChoose(alternative.supplier)}>{alternative.supplier} wählen</Button>
                  ) : null}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {/* Optional on the generated type (fix round 2, item 13) — the same
          `?? null` tolerance `ApprovalPanel` already applies to
          `PriceStateSeenOut`'s fields, so an omitted key never reads as "an
          override stands" the way `undefined !== null` would. */}
      {!disabled && (calc.params.supplierOverride ?? null) !== null ? (
        <Button variant="quiet" onClick={() => onChoose(null)}>
          Vorschlag wiederherstellen
        </Button>
      ) : null}
    </div>
  );
}
